import type {
  AdminGame,
  AdminGameVersion,
  NzbInfo,
  NzbStatus,
  UpdateGameVersionRequest,
} from '@mcgames/contracts';
import { Badge, Button, TextInput } from '@mcgames/ui';
import { useId, useState, type DragEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatBytes } from '../../lib/format-bytes.ts';
import { ErrorText } from './ErrorText.tsx';
import { FileButton } from './FileButton.tsx';
import styles from './Games.module.css';
import { useVersionMutation } from './version-hooks.ts';

const GIB = 1024 ** 3;

function statusTone(status: NzbStatus): 'default' | 'accent' | 'danger' {
  if (status === 'ok') return 'accent';
  return status === 'checking' || status === 'repairable' ? 'default' : 'danger';
}

export type VersionNzbCardProps = {
  game: AdminGame;
  version: AdminGameVersion;
};

/** The NZB of one version: its check result and files, a re-check, a replacement upload and the size estimate. */
export function VersionNzbCard({ game, version }: VersionNzbCardProps) {
  const { t } = useTranslation('admin');
  const titleId = useId();

  return (
    <section className={styles['card']} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles['subheading']}>
        {t('games.nzb.title')}
      </h3>
      {version.nzb === null ? (
        <p className={styles['muted']}>{t('games.nzb.noNzb')}</p>
      ) : (
        <NzbDetails game={game} version={version} nzb={version.nzb} />
      )}
      <ReplaceNzb game={game} version={version} />
      <InstallSizeEstimate key={version.installSizeEstimate} game={game} version={version} />
    </section>
  );
}

function ReplaceNzb({ game, version }: VersionNzbCardProps) {
  const { t } = useTranslation('admin');
  const upload = useVersionMutation<FormData>(game.id, version.id, 'POST', '/nzb');
  const [dragging, setDragging] = useState(false);

  function send(file: File | undefined) {
    if (file === undefined) return;
    const form = new FormData();
    form.append('file', file);
    upload.mutate(form);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    send(event.dataTransfer.files[0]);
  }

  return (
    <>
      <div
        data-testid="nzb-dropzone"
        className={[styles['dropzone'], dragging ? styles['dropActive'] : ''].join(' ')}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <p className={styles['muted']}>{t('games.nzb.dropzone')}</p>
        <FileButton
          label={t('games.nzb.chooseFile')}
          accept=".nzb,application/x-nzb,text/xml"
          disabled={upload.isPending}
          onFiles={([file]) => send(file)}
        />
        {version.nzb !== null && <p className={styles['hint']}>{t('games.nzb.replaceHint')}</p>}
        {upload.isPending && <p className={styles['hint']}>{t('games.nzb.uploading')}</p>}
      </div>
      <ErrorText error={upload.error} />
    </>
  );
}

function NzbDetails({ game, version, nzb }: VersionNzbCardProps & { nzb: NzbInfo }) {
  const { t, i18n } = useTranslation('admin');
  const formatBytes = useFormatBytes();
  const recheck = useVersionMutation<void>(game.id, version.id, 'POST', '/nzb/check');
  const blocks = { needed: nzb.neededBlocks, recovery: nzb.recoveryBlocks };

  return (
    <>
      <dl className={styles['facts']}>
        <dt>{t('games.nzb.fileName')}</dt>
        <dd>{nzb.fileName}</dd>
        <dt>{t('games.nzb.status')}</dt>
        <dd>
          <div className={styles['row']}>
            <Badge tone={statusTone(nzb.status)}>{t(`games.nzb.state.${nzb.status}`)}</Badge>
            {nzb.checkedAt !== null && (
              <span className={styles['hint']}>
                {t('games.nzb.checkedAt', {
                  date: new Date(nzb.checkedAt).toLocaleString(i18n.language),
                })}
              </span>
            )}
          </div>
        </dd>
      </dl>
      {nzb.status === 'repairable' && (
        <>
          <p className={styles['hint']}>{t('games.nzb.repairable', blocks)}</p>
          <p className={styles['hint']}>
            {t('games.nzb.missing', { missing: nzb.missingArticles, total: nzb.totalArticles })}
          </p>
          <p className={styles['hint']}>{t('games.nzb.repairableHint')}</p>
        </>
      )}
      {nzb.status === 'missing' && (
        <>
          <p className={styles['error']}>
            {t('games.nzb.missing', { missing: nzb.missingArticles, total: nzb.totalArticles })}
          </p>
          {(nzb.recoveryBlocks > 0 || nzb.neededBlocks > 0) && (
            <p className={styles['error']}>{t('games.nzb.notRepairable', blocks)}</p>
          )}
        </>
      )}
      {nzb.status === 'checking' && <p className={styles['hint']}>{t('games.nzb.checkingHint')}</p>}
      {nzb.status === 'error' && <p className={styles['error']}>{t('games.nzb.errorHint')}</p>}
      <div className={styles['row']}>
        <Button variant="secondary" disabled={recheck.isPending} onClick={() => recheck.mutate()}>
          {t('games.nzb.recheck')}
        </Button>
      </div>
      <ErrorText error={recheck.error} />
      <h4 className={styles['subheading']}>{t('games.nzb.files')}</h4>
      <ul className={styles['fileList']}>
        {nzb.files.map((file) => (
          <li key={file.name}>
            <span className={styles['fileName']}>{file.name}</span>
            <span className={styles['fileSize']}>{formatBytes(file.size)}</span>
          </li>
        ))}
      </ul>
      <p className={styles['muted']}>
        {t('games.nzb.totalSize', { size: formatBytes(nzb.downloadSize) })}
      </p>
      <p className={styles['muted']}>
        {/* The client keeps the download and the installed game on disk at the same time. */}
        {t('games.nzb.tempSpace', {
          size: formatBytes(nzb.downloadSize + version.installSizeEstimate),
        })}
      </p>
    </>
  );
}

function InstallSizeEstimate({ game, version }: VersionNzbCardProps) {
  const { t } = useTranslation('admin');
  const save = useVersionMutation<UpdateGameVersionRequest>(game.id, version.id, 'PATCH');
  const initial = Math.round((version.installSizeEstimate / GIB) * 100) / 100;
  const [gigabytes, setGigabytes] = useState(initial.toString());
  const value = gigabytes === '' ? null : Number(gigabytes);
  const valid = value !== null && Number.isFinite(value) && value >= 0;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid) save.mutate({ installSizeEstimate: Math.round(value * GIB) });
  }

  return (
    <form className={styles['form']} onSubmit={submit}>
      <div className={styles['row']}>
        <div className={styles['sizeInput']}>
          <TextInput
            label={t('games.nzb.installSize')}
            type="number"
            min={0}
            step="any"
            value={gigabytes}
            onChange={(event) => setGigabytes(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={!valid || value === initial || save.isPending}>
          {t('games.nzb.saveSize')}
        </Button>
      </div>
      <span className={styles['hint']}>{t('games.nzb.installSizeHint')}</span>
      <ErrorText error={save.error} />
    </form>
  );
}
